import React, { useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Typography,
  useTheme,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  Divider,
  Chip,
  Stack,
  IconButton,
  Tooltip,
  TextField,
  FormControl,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  CircularProgress,
  TablePagination,
  TableSortLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputLabel,
  Skeleton,
} from "@mui/material";
import OrbLoader from "../components/common/OrbLoader";
import { useTranslation } from "react-i18next";
import { useSelector, useDispatch } from "react-redux";
import { Navigate, useNavigate, useSearchParams } from "react-router-dom";
import AddIcon from "@mui/icons-material/Add";
import RefreshIcon from "@mui/icons-material/Refresh";
import EditIcon from "@mui/icons-material/Edit";
import DownloadIcon from "@mui/icons-material/Download";
import DeleteIcon from "@mui/icons-material/Delete";
import LaunchIcon from "@mui/icons-material/Launch";
import BusinessIcon from "@mui/icons-material/Business";
import {
  useGetCompaniesQuery,
  useGetUserCompaniesQuery,
} from "../store/apis/companyApi";
import VisibilityIcon from "@mui/icons-material/Visibility";
import SyncIcon from "@mui/icons-material/Sync";
import HistoryIcon from "@mui/icons-material/History";

import { selectSidebarCollapsed } from "../store/slices/app/appSelector";
import { setToastMessage } from "../store/slices/app/appSlice";
import {
  selectUserDb,
  selectUser,
} from "../store/slices/session/sessionSelector";
import {
  useListBriefingsQuery,
  useDeleteBriefingMutation,
  useSyncCampaignFinancialsMutation,
} from "../store/apis/briefingApi";
import { components, operations } from "../types/api";
import BriefingSummaryDrawer from "../components/briefings/BriefingSummaryDrawer";
import ConfirmationModal from "../components/modals/ConfirmationModal";
import { useContextMenu } from "../hooks/useContextMenu";
import { TableRowContextMenu } from "../components/common/TableRowContextMenu";
import { BRIEFING_STATUS_COLORS } from "../components/common/StatusChips";
import LetterBadge from "../components/common/LetterBadge";
import PageBreadcrumbs from "../components/layout/PageBreadcrumbs";
import MonthYearFilter from "../components/briefings/MonthYearFilter";
import {
  formatShortDate,
  shortMonth,
  shortMonthYear,
} from "../utils/briefings/dateRange";
import { shortTimeAgo } from "../utils/relativeTime";
import { middleClickOpen } from "../utils/middleClickOpen";
import NumberField from "../components/common/NumberField";
import { createNumberFormat } from "../utils/numberFormat";
import AssignedUserFilter from "../components/briefings/AssignedUserFilter";

type BriefingCategory = components["schemas"]["_36_Enums.BriefingCategory"];

type BriefingType = components["schemas"]["_36_Enums.BriefingType"];

type BriefingSortBy = NonNullable<
  NonNullable<operations["ListBriefings"]["parameters"]["query"]>["sortBy"]
>;

const CATEGORY_OPTIONS: BriefingCategory[] = ["MONTHLY", "BIMONTHLY", "ANNUAL"];

// Non-filter keys that also live in the URL.
const URL_STATE_KEYS = ["page", "pageSize", "sortBy", "sortDir"];

const TYPE_OPTIONS: BriefingType[] = [
  "PAID_MEDIA_MANAGED",
  "BUY_MEDIA_DIRECT_AND_OWN",
];

// Three light grey tones so the period badges can be told apart at a glance.
// theme.palette.grey is inverted in dark mode, so they stay subtle there too.
const CATEGORY_TONES: Record<BriefingCategory, 100 | 200 | 300> = {
  MONTHLY: 100,
  BIMONTHLY: 200,
  ANNUAL: 300,
};

// Local YYYY-MM-DD formatter (avoids UTC shift from toISOString near month edges)
const toYMD = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
    d.getDate()
  ).padStart(2, "0")}`;

const firstDayOfCurrentMonth = () => {
  const now = new Date();
  return toYMD(new Date(now.getFullYear(), now.getMonth(), 1));
};

const lastDayOfCurrentMonth = () => {
  const now = new Date();
  return toYMD(new Date(now.getFullYear(), now.getMonth() + 1, 0));
};

const Briefings: React.FC = () => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const collapsed = useSelector(selectSidebarCollapsed);
  const userDb = useSelector(selectUserDb);
  const canCreateBriefings =
    userDb?.role === "ADMIN" || userDb?.role === "ACCOUNT";
  const canViewBriefings =
    canCreateBriefings ||
    userDb?.role === "TECHNICIAN" ||
    userDb?.role === "PRICING";
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const defaultFilters = {
    companyId: "",
    search: "",
    month: "",
    year: "",
    minBudget: "",
    maxBudget: "",
    status: [] as string[],
    category: "",
    briefingType: "",
    // Persona asignada a la empresa (partner success o responsable de plataforma)
    assignedUserId: "",
  };

  const getSavedState = () => {
    try {
      const saved = localStorage.getItem("beapolo_briefings_filters");
      if (saved) return JSON.parse(saved);
    } catch (e) {
      console.error("Failed to parse saved filters", e);
    }
    return null;
  };

  const [searchParams, setSearchParams] = useSearchParams();

  // The URL wins over localStorage, so going back to a filtered list (or
  // opening a shared link) shows exactly that list.
  const getUrlState = () => {
    const keys = [...Object.keys(defaultFilters), ...URL_STATE_KEYS];
    if (!keys.some((k) => searchParams.has(k))) return null;
    const filters: Record<string, string | string[]> = {};
    Object.keys(defaultFilters).forEach((k) => {
      if (k === "status") filters.status = searchParams.getAll("status");
      else filters[k] = searchParams.get(k) ?? "";
    });
    const num = (k: string) => {
      const n = Number(searchParams.get(k));
      return searchParams.has(k) && Number.isFinite(n) ? n : undefined;
    };
    return {
      filters,
      page: num("page") !== undefined ? num("page")! - 1 : undefined,
      pageSize: num("pageSize"),
      sortBy: searchParams.get("sortBy") || undefined,
      sortDir: searchParams.get("sortDir") || undefined,
    };
  };

  const savedState = getUrlState() ?? getSavedState();

  // Migrate saved filters: status was a string before multi-select, now it must be string[]
  const migratedFilters = React.useMemo(() => {
    if (!savedState?.filters) return defaultFilters;
    const saved = savedState.filters;
    return {
      ...defaultFilters,
      ...saved,
      status: Array.isArray(saved.status)
        ? saved.status
        : saved.status
          ? [saved.status]
          : [],
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const [filters, setFilters] =
    useState<typeof defaultFilters>(migratedFilters);
  const [page, setPage] = useState<number>(savedState?.page || 0);
  const [pageSize, setPageSize] = useState<number>(savedState?.pageSize || 25);
  const [sortBy, setSortBy] = useState<BriefingSortBy>(
    savedState?.sortBy || "updatedAt"
  );
  const [sortDir, setSortDir] = useState<"asc" | "desc">(
    savedState?.sortDir || "desc"
  );

  React.useEffect(() => {
    localStorage.setItem(
      "beapolo_briefings_filters",
      JSON.stringify({ filters, page, pageSize, sortBy, sortDir })
    );

    const params = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => {
      if (Array.isArray(v)) v.forEach((x) => params.append(k, x));
      else if (v) params.set(k, String(v));
    });
    if (page > 0) params.set("page", String(page + 1));
    if (pageSize !== 25) params.set("pageSize", String(pageSize));
    if (sortBy !== "updatedAt") params.set("sortBy", sortBy);
    if (sortDir !== "desc") params.set("sortDir", sortDir);
    // replace: typing in a filter must not stack history entries
    setSearchParams(params, { replace: true });
  }, [filters, page, pageSize, sortBy, sortDir, setSearchParams]);

  const [selectedBriefingId, setSelectedBriefingId] = React.useState<
    string | null
  >(null);
  const [isExporting, setIsExporting] = useState(false);
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [exportStartDate, setExportStartDate] = useState<string>(
    firstDayOfCurrentMonth
  );
  const [exportEndDate, setExportEndDate] = useState<string>(
    lastDayOfCurrentMonth
  );
  // "pricing" = the per-briefing pricing sheet; "closing" = the month-end sheet
  // with one row per associated campaign and planned vs real with margin.
  const [exportVariant, setExportVariant] = useState<"pricing" | "closing">(
    "pricing"
  );
  const user = useSelector(selectUser);

  const { contextMenu, handleContextMenu, handleCloseContextMenu } =
    useContextMenu<(typeof briefings)[0]>();
  const [briefingToDelete, setBriefingToDelete] = useState<string | null>(null);
  const [deleteBriefing, { isLoading: isDeleting }] =
    useDeleteBriefingMutation();

  const [syncFinancials, { isLoading: isSyncing }] =
    useSyncCampaignFinancialsMutation();
  const [isSyncModalOpen, setIsSyncModalOpen] = useState(false);

  const isAdmin = userDb?.role === "ADMIN";
  const canExportBriefings =
    userDb?.role === "ADMIN" || userDb?.role === "PRICING";

  // Estados visibles por defecto en la lista (todos menos ARCHIVED).
  // Cuando el usuario no selecciona ningún estado, se filtran estos para
  // ocultar los briefings archivados.
  const defaultStatuses = React.useMemo<
    components["schemas"]["BriefingStatus"][]
  >(
    () => [
      "DRAFT",
      "READY_FOR_PRICING",
      "READY_FOR_PRODUCTION",
      "IN_PRODUCTION",
      "FINALIZED",
      "UPDATING",
    ],
    []
  );

  // Opciones del filtro de estado: incluye ARCHIVED para que el usuario
  // pueda mostrar explícitamente los briefings archivados.
  const statusOptions = React.useMemo<
    components["schemas"]["BriefingStatus"][]
  >(() => [...defaultStatuses, "ARCHIVED"], [defaultStatuses]);

  const queryParams = React.useMemo(
    () => ({
      page: page + 1, // API is likely 1-indexed
      pageSize,
      companyId: filters.companyId || undefined,
      search: filters.search.trim() || undefined,
      // Sin selección explícita ocultamos los archivados enviando solo los
      // estados por defecto; si el usuario filtra (incluyendo ARCHIVED) se
      // respeta su selección.
      status:
        filters.status && filters.status.length > 0
          ? (filters.status as components["schemas"]["BriefingStatus"][])
          : defaultStatuses,
      month: filters.month ? Number(filters.month) : undefined,
      year: filters.year ? Number(filters.year) : undefined,
      minBudget: filters.minBudget ? Number(filters.minBudget) : undefined,
      maxBudget: filters.maxBudget ? Number(filters.maxBudget) : undefined,
      category: filters.category
        ? [filters.category as BriefingCategory]
        : undefined,
      briefingType: filters.briefingType
        ? (filters.briefingType as BriefingType)
        : undefined,
      assignedUserId: filters.assignedUserId || undefined,
      sortBy,
      sortDir,
    }),
    [filters, page, pageSize, sortBy, sortDir, defaultStatuses]
  );

  // Todos los clientes para el filtro. /companies solo lo pueden leer algunos
  // roles; el resto (p. ej. TECHNICIAN) ve los suyos.
  const canListAllCompanies =
    userDb?.role === "ADMIN" ||
    userDb?.role === "STRATEGY" ||
    userDb?.role === "PRICING" ||
    userDb?.role === "ACCOUNT";
  const { data: allCompaniesResp } = useGetCompaniesQuery(undefined, {
    skip: !canViewBriefings || !canListAllCompanies,
  });
  const { data: userCompaniesResp } = useGetUserCompaniesQuery(undefined, {
    skip: !canViewBriefings || canListAllCompanies,
  });
  const companyFilterOptions = React.useMemo(() => {
    const list =
      (canListAllCompanies ? allCompaniesResp : userCompaniesResp)?.data ?? [];
    const options = list
      .map((company): { id: string; name: string; image: string | null } => ({
        id: company.id,
        name: company.nameCommercial || company.nameFiscal || company.id,
        image: company.imageBase64 ?? null,
      }))
      .sort((a, b) => a.name.localeCompare(b.name, "es"));
    // Filtro guardado/de la URL cuyo cliente aún no ha cargado (o no está en la lista).
    if (filters.companyId && !options.some((c) => c.id === filters.companyId)) {
      options.push({
        id: filters.companyId,
        name: filters.companyId,
        image: null,
      });
    }
    return options;
  }, [
    canListAllCompanies,
    allCompaniesResp,
    userCompaniesResp,
    filters.companyId,
  ]);

  // `currentData` es la caché para estos filtros: si existe se pinta al
  // instante y el refetch va en segundo plano; el loader solo sale cuando no
  // hay nada guardado para la combinación de filtros/página actual.
  const { currentData, isFetching, error, refetch } = useListBriefingsQuery(
    queryParams,
    {
      refetchOnMountOrArgChange: true,
      skip: !canViewBriefings,
    }
  );

  const briefings = currentData?.data?.items || [];
  const total = currentData?.data?.total || 0;
  const showLoader = !currentData && isFetching;
  const isEmpty = !showLoader && total === 0;

  const errorMessage =
    error && "data" in error && error.data && typeof error.data === "object"
      ? "message" in error.data
        ? String(error.data.message)
        : t("briefings.errorLoading", "Failed to load briefings")
      : error
        ? t("briefings.errorLoading", "Failed to load briefings")
        : null;

  const handleFilterChange = (field: string, value: string | string[]) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
    setPage(0); // Reset to first page on filter change
  };

  const handleSort = (field: BriefingSortBy) => {
    const isAsc = sortBy === field && sortDir === "asc";
    setSortDir(isAsc ? "desc" : "asc");
    setSortBy(field);
    setPage(0);
  };

  const renderSortLabel = (field: BriefingSortBy, label: string) => (
    <TableSortLabel
      active={sortBy === field}
      direction={sortBy === field ? sortDir : "asc"}
      onClick={() => handleSort(field)}
    >
      {label}
    </TableSortLabel>
  );

  const handleConfirmDelete = async () => {
    if (!briefingToDelete) return;
    try {
      await deleteBriefing({ id: briefingToDelete }).unwrap();
      dispatch(
        setToastMessage({
          message: t(
            "briefings.deleteSuccess",
            "Briefing deleted successfully"
          ),
          severity: "success",
        })
      );
      if (selectedBriefingId === briefingToDelete) setSelectedBriefingId(null);
      refetch();
    } catch (error) {
      console.error(error);
      dispatch(
        setToastMessage({
          message: t("briefings.deleteError", "Unable to delete briefing"),
          severity: "error",
        })
      );
    } finally {
      setBriefingToDelete(null);
    }
  };

  const handleExportControlPanel = async () => {
    if (!exportStartDate || !exportEndDate) {
      dispatch(
        setToastMessage({
          message: t(
            "briefings.exportDateRangeRequired",
            "Please select a start and end date."
          ),
          severity: "warning",
        })
      );
      return;
    }
    if (exportEndDate < exportStartDate) {
      dispatch(
        setToastMessage({
          message: t(
            "briefings.exportEndBeforeStart",
            "End date cannot be before start date."
          ),
          severity: "warning",
        })
      );
      return;
    }
    try {
      setIsExporting(true);
      const url = new URLSearchParams();
      if (queryParams.companyId) url.append("companyId", queryParams.companyId);
      if (queryParams.search) url.append("search", queryParams.search);
      if (queryParams.status) {
        queryParams.status.forEach((st) => url.append("status", st));
      }
      if (queryParams.briefingType)
        url.append("briefingType", queryParams.briefingType);
      if (exportStartDate) url.append("startDate", exportStartDate);
      if (exportEndDate) url.append("endDate", exportEndDate);
      if (queryParams.minBudget)
        url.append("minBudget", String(queryParams.minBudget));
      if (queryParams.maxBudget)
        url.append("maxBudget", String(queryParams.maxBudget));
      url.append("variant", exportVariant);

      const API_URL = process.env.REACT_APP_API_BACKEND_URL;
      const response = await fetch(
        `${API_URL}/api/reports/pricing-export/xlsx?${url.toString()}`,
        {
          headers: {
            Authorization: `Bearer ${user?.token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error("Export failed");
      }

      // The backend now returns a ready-to-use .xlsx file (binary), so we just
      // download the blob directly — no client-side CSV building needed.
      const blob = await response.blob();

      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.download =
        exportVariant === "closing"
          ? "Cierre_Mes_Export.xlsx"
          : "Pricing_Export.xlsx";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
    } catch (err) {
      console.error("Export error", err);
    } finally {
      setIsExporting(false);
    }
  };

  const handleSyncFinancials = async () => {
    try {
      const res = await syncFinancials().unwrap();
      dispatch(
        setToastMessage({
          message: t(
            "briefings.syncSuccess",
            "Financial sync completed successfully. Processed {{count}} goals.",
            { count: res.processed }
          ),
          severity: "success",
        })
      );
      refetch();
    } catch (error) {
      console.error("Sync error:", error);
      dispatch(
        setToastMessage({
          message: t(
            "briefings.syncFailed",
            "Failed to sync campaign financials"
          ),
          severity: "error",
        })
      );
    } finally {
      setIsSyncModalOpen(false);
    }
  };

  const numberFormatter = createNumberFormat({
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });

  const selectSx = {
    backgroundColor: theme.palette.background.paper,
    borderRadius: "20px",
    "& fieldset": { borderColor: theme.palette.grey[300] },
  };

  // Company logo, or a generic icon when it has none.
  const renderCompanyAvatar = (
    image: string | null | undefined,
    size: number
  ) =>
    image ? (
      <Box
        component="img"
        src={
          image.startsWith("data:") ? image : `data:image/png;base64,${image}`
        }
        alt=""
        sx={{
          width: size,
          height: size,
          flexShrink: 0,
          objectFit: "cover",
          borderRadius: 9999,
        }}
      />
    ) : (
      <Box
        sx={{
          width: size,
          height: size,
          flexShrink: 0,
          borderRadius: 9999,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: theme.palette.grey[200],
          color: theme.palette.grey[500],
        }}
      >
        <BusinessIcon sx={{ fontSize: size * 0.57 }} />
      </Box>
    );

  const renderCategoryBadge = (category: BriefingCategory) => (
    <LetterBadge
      letter={t(`briefings.categoryShort.${category}`, category.charAt(0))}
      title={t(`briefings.category.${category}`, category)}
      sx={{
        backgroundColor: theme.palette.grey[CATEGORY_TONES[category]],
        border: `1px solid ${theme.palette.grey[400]}`,
        color: "text.primary",
      }}
    />
  );

  // Pill with a short uppercase label (PAID / BUY); full name in the tooltip.
  const renderTypeBadge = (type: BriefingType) => {
    const isPaid = type === "PAID_MEDIA_MANAGED";
    // PAID is teal, BUY a warm amber, so the two tell apart at a glance. In
    // light mode both are dark enough to read on white (about 5:1); darkening
    // the brand yellow itself turns it olive, so BUY uses a saturated ochre
    // instead. Dark mode uses lighter shades that read on a dark surface.
    const isLight = theme.palette.mode === "light";
    const color = isPaid
      ? isLight
        ? "#007C89"
        : "#4FC3B8"
      : isLight
        ? "#9A6B00"
        : theme.palette.secondary.main;
    const label = t(`briefingsCreate.form.briefingTypeOptions.${type}`, type);
    return (
      <Tooltip title={label}>
        <Box
          component="span"
          aria-label={label}
          sx={{
            display: "inline-flex",
            alignItems: "center",
            px: 0.75,
            height: 18,
            borderRadius: "9px",
            border: `1px solid ${color}`,
            color,
            fontSize: "0.625rem",
            fontWeight: 700,
            letterSpacing: "0.04em",
            textTransform: "uppercase",
            lineHeight: 1,
            cursor: "default",
          }}
        >
          {t(`briefings.typeShort.${type}`, type)}
        </Box>
      </Tooltip>
    );
  };

  const renderStatusBadge = (
    status: components["schemas"]["BriefingStatus"]
  ) => {
    const color = BRIEFING_STATUS_COLORS[status];
    const paletteColor =
      color && color !== "default" ? theme.palette[color] : null;
    const label = t(`briefings.status.${status}`, status);
    // Pill with a short label; "Ready\nPricing" style labels wrap to two lines.
    return (
      <Tooltip title={label}>
        <Box
          component="span"
          aria-label={label}
          sx={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            minWidth: 44,
            minHeight: 20,
            px: 0.75,
            py: 0.25,
            borderRadius: "10px",
            backgroundColor: paletteColor
              ? paletteColor.main
              : theme.palette.grey[300],
            color: paletteColor ? paletteColor.contrastText : "text.primary",
            fontSize: "0.625rem",
            fontWeight: 700,
            lineHeight: 1.15,
            textAlign: "center",
            whiteSpace: "pre-line",
            cursor: "default",
          }}
        >
          {t(`briefings.statusShort.${status}`, status)}
        </Box>
      </Tooltip>
    );
  };

  if (!canViewBriefings) {
    return <Navigate to="/" replace />;
  }

  return (
    <Box sx={{ display: "flex", minHeight: "100vh" }}>
      <Box
        sx={{
          flexGrow: 1,
          minWidth: 0,
          px: { xs: 2, md: "50px" },
          py: { xs: 2, md: 3 },
          transition: "margin-left 0.3s ease",
          ...(collapsed
            ? { ml: `calc(${theme.spacing(7)} - 4px)` }
            : { ml: `calc(${theme.spacing(32)} + 12px)` }),
          display: "flex",
          flexDirection: "column",
          gap: 3,
        }}
      >
        <PageBreadcrumbs />

        <Paper
          elevation={0}
          sx={{
            borderRadius: 3,
            border: `1px solid ${theme.palette.grey[200]}`,
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Box
            sx={{
              px: { xs: 2, md: 3 },
              py: 2,
              display: "flex",
              justifyContent: "space-between",
              alignItems: { xs: "flex-start", sm: "center" },
              flexDirection: { xs: "column", sm: "row" },
              gap: 1,
            }}
          >
            <Stack
              direction={{ xs: "column", sm: "row" }}
              alignItems={{ xs: "flex-start", sm: "center" }}
              gap={2}
            >
              <AssignedUserFilter
                value={filters.assignedUserId}
                onChange={(userId) =>
                  handleFilterChange("assignedUserId", userId)
                }
                currentUserId={userDb?.id}
              />
              <Typography variant="body2" color="text.secondary">
                {t("briefings.totalLabel", "{{count}} total", { count: total })}
              </Typography>
            </Stack>
            <Stack direction="row" spacing={1} alignItems="center">
              <Tooltip title={t("briefings.refreshTooltip", "Refresh list")}>
                <span>
                  <IconButton
                    color="primary"
                    onClick={() => refetch()}
                    disabled={isFetching || isExporting}
                    sx={{
                      border: `1px solid ${theme.palette.primary.light}`,
                      "&:disabled": {
                        opacity: 0.5,
                      },
                    }}
                  >
                    {isFetching ? <OrbLoader size="inline" /> : <RefreshIcon />}
                  </IconButton>
                </span>
              </Tooltip>
              {canExportBriefings && (
                <Button
                  variant="outlined"
                  color="secondary"
                  startIcon={
                    isExporting ? (
                      <CircularProgress size={20} />
                    ) : (
                      <DownloadIcon />
                    )
                  }
                  onClick={() => setIsExportModalOpen(true)}
                  disabled={isExporting}
                  sx={{
                    borderRadius: "50px",
                    px: 3,
                    fontWeight: 700,
                    boxShadow: "0px 4px 8px rgba(0,0,0,0.05)",
                  }}
                >
                  {isExporting
                    ? t("briefings.exporting", "Generando CSV...")
                    : t("briefings.exportControlPanel", "Export Pricing CSV")}
                </Button>
              )}
              {canCreateBriefings && (
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<AddIcon />}
                  onClick={() => navigate("/briefings/new")}
                  sx={{
                    borderRadius: "50px",
                    px: 3,
                    fontWeight: 700,
                    boxShadow: "0px 8px 16px rgba(0,0,0,0.08)",
                  }}
                >
                  {t("briefings.create", "Create briefing")}
                </Button>
              )}
              {isAdmin && (
                <Button
                  variant="outlined"
                  color="info"
                  startIcon={<SyncIcon />}
                  onClick={() => setIsSyncModalOpen(true)}
                  disabled={isSyncing}
                  sx={{
                    borderRadius: "50px",
                    px: 3,
                    fontWeight: 700,
                    boxShadow: "0px 4px 8px rgba(0,0,0,0.05)",
                  }}
                >
                  {isSyncing
                    ? t("common.syncing", "Syncing...")
                    : t("briefings.syncFinancials", "Sync Platform Financials")}
                </Button>
              )}
            </Stack>
          </Box>
          <Divider />
          {errorMessage && (
            <Box sx={{ px: { xs: 2, md: 3 }, py: 2 }}>
              <Alert severity="error">{errorMessage}</Alert>
            </Box>
          )}

          <TableContainer sx={{ overflowX: "auto" }}>
            <Table
              size="small"
              sx={{
                "& .MuiTableCell-root": { px: 2 },
                "& .MuiTableBody-root .MuiTableCell-root": { py: 1.5 },
              }}
            >
              <TableHead>
                <TableRow sx={{ backgroundColor: theme.palette.grey[50] }}>
                  <TableCell
                    sx={{ width: "22%", maxWidth: 220, fontWeight: 700 }}
                  >
                    {renderSortLabel(
                      "client",
                      t("briefings.columns.client", "Client")
                    )}
                  </TableCell>
                  <TableCell
                    sx={{ width: "35%", minWidth: 220, fontWeight: 700 }}
                  >
                    {renderSortLabel(
                      "name",
                      t("briefings.columns.briefingName", "Briefing name")
                    )}
                  </TableCell>
                  <TableCell sx={{ width: 90, fontWeight: 700 }} align="center">
                    {renderSortLabel(
                      "type",
                      t("briefings.columns.type", "Type")
                    )}
                  </TableCell>
                  <TableCell sx={{ width: 90, fontWeight: 700 }} align="center">
                    {renderSortLabel(
                      "category",
                      t("briefings.columns.category", "Period")
                    )}
                  </TableCell>
                  <TableCell sx={{ width: 90, fontWeight: 700 }} align="center">
                    {renderSortLabel(
                      "status",
                      t("briefings.columns.status", "Status")
                    )}
                  </TableCell>
                  <TableCell sx={{ minWidth: 130, fontWeight: 700 }}>
                    <TableSortLabel
                      active={sortBy === "year"}
                      direction={sortBy === "year" ? sortDir : "asc"}
                      onClick={() => handleSort("year")}
                    >
                      {t("briefings.columns.date", "Dates")}
                    </TableSortLabel>
                  </TableCell>
                  <TableCell
                    sx={{ minWidth: 120, fontWeight: 700 }}
                    align="right"
                  >
                    {t("briefings.columns.budget", "Planned investment")}
                  </TableCell>
                  <TableCell sx={{ width: 110, fontWeight: 700 }}>
                    <Tooltip
                      title={t("briefings.columns.lastUpdated", "Last change")}
                    >
                      <TableSortLabel
                        active={sortBy === "updatedAt"}
                        direction={sortBy === "updatedAt" ? sortDir : "asc"}
                        onClick={() => handleSort("updatedAt")}
                        aria-label={t(
                          "briefings.columns.lastUpdated",
                          "Last change"
                        )}
                      >
                        <HistoryIcon fontSize="small" />
                      </TableSortLabel>
                    </Tooltip>
                  </TableCell>
                </TableRow>
                {/* Filter Row */}
                <TableRow sx={{ backgroundColor: theme.palette.grey[50] }}>
                  <TableCell sx={{ py: 1 }}>
                    <Autocomplete
                      size="small"
                      fullWidth
                      options={companyFilterOptions}
                      value={
                        companyFilterOptions.find(
                          (c) => c.id === filters.companyId
                        ) ?? null
                      }
                      onChange={(_, company) =>
                        handleFilterChange("companyId", company?.id ?? "")
                      }
                      getOptionLabel={(company) => company.name}
                      isOptionEqualToValue={(a, b) => a.id === b.id}
                      noOptionsText={t("common.noResults", "No results")}
                      ListboxProps={{ style: { maxHeight: 360 } }}
                      renderOption={(props, company) => (
                        <li {...props} key={company.id}>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1.25,
                            }}
                          >
                            {renderCompanyAvatar(company.image, 24)}
                            <Typography variant="body2">
                              {company.name}
                            </Typography>
                          </Box>
                        </li>
                      )}
                      renderInput={(params) => {
                        const selected = companyFilterOptions.find(
                          (c) => c.id === filters.companyId
                        );
                        return (
                          <TextField
                            {...params}
                            placeholder={t("common.all", "All")}
                            sx={selectSx}
                            InputProps={{
                              ...params.InputProps,
                              startAdornment: selected ? (
                                <Box sx={{ display: "flex", ml: 0.5 }}>
                                  {renderCompanyAvatar(selected.image, 20)}
                                </Box>
                              ) : null,
                            }}
                          />
                        );
                      }}
                    />
                  </TableCell>
                  <TableCell sx={{ py: 1 }}>
                    <TextField
                      size="small"
                      fullWidth
                      variant="outlined"
                      value={filters.search}
                      onChange={(e) =>
                        handleFilterChange("search", e.target.value)
                      }
                      sx={{
                        backgroundColor: theme.palette.background.paper,
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "20px",
                          "& fieldset": {
                            borderColor: theme.palette.grey[300],
                          },
                        },
                      }}
                    />
                  </TableCell>
                  <TableCell sx={{ py: 1 }}>
                    <FormControl fullWidth size="small">
                      <Select
                        value={filters.briefingType}
                        onChange={(e) =>
                          handleFilterChange(
                            "briefingType",
                            e.target.value as string
                          )
                        }
                        renderValue={(selected) =>
                          t(`briefings.typeShort.${selected}`, selected)
                        }
                        sx={selectSx}
                      >
                        <MenuItem value="">
                          <em>{t("common.all", "All")}</em>
                        </MenuItem>
                        {TYPE_OPTIONS.map((type) => (
                          <MenuItem key={type} value={type}>
                            {t(
                              `briefingsCreate.form.briefingTypeOptions.${type}`,
                              type
                            )}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </TableCell>
                  <TableCell sx={{ py: 1 }}>
                    <FormControl fullWidth size="small">
                      <Select
                        value={filters.category}
                        onChange={(e) =>
                          handleFilterChange(
                            "category",
                            e.target.value as string
                          )
                        }
                        renderValue={(selected) =>
                          t(`briefings.categoryShort.${selected}`, selected)
                        }
                        sx={selectSx}
                      >
                        <MenuItem value="">
                          <em>{t("common.all", "All")}</em>
                        </MenuItem>
                        {CATEGORY_OPTIONS.map((cat) => (
                          <MenuItem key={cat} value={cat}>
                            {t(`briefings.category.${cat}`, cat)}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </TableCell>
                  <TableCell sx={{ py: 1 }}>
                    <FormControl fullWidth size="small">
                      <Select
                        multiple
                        value={filters.status}
                        onChange={(e) => {
                          const val = e.target.value;
                          handleFilterChange(
                            "status",
                            typeof val === "string" ? val.split(",") : val
                          );
                        }}
                        renderValue={(selected) =>
                          (selected as string[])
                            .map((opt) =>
                              t(`briefings.statusShort.${opt}`, opt).replace(
                                "\n",
                                " "
                              )
                            )
                            .join(", ")
                        }
                        sx={selectSx}
                      >
                        {statusOptions.map((option) => (
                          <MenuItem key={option} value={option}>
                            <Checkbox
                              checked={filters.status.indexOf(option) > -1}
                            />
                            <ListItemText
                              primary={t(`briefings.status.${option}`, option)}
                            />
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </TableCell>
                  <TableCell sx={{ py: 1 }}>
                    <MonthYearFilter
                      month={String(filters.month)}
                      year={String(filters.year)}
                      formatMonth={(m) => shortMonth(m, i18n.language)}
                      onChange={({ month, year }) => {
                        handleFilterChange("month", month);
                        handleFilterChange("year", year);
                      }}
                    />
                  </TableCell>
                  <TableCell sx={{ py: 1 }}>
                    <NumberField
                      size="small"
                      fullWidth
                      variant="outlined"
                      value={filters.minBudget}
                      onChange={(e) =>
                        handleFilterChange("minBudget", e.target.value)
                      }
                      sx={{
                        backgroundColor: theme.palette.background.paper,
                        "& input": { textAlign: "right" },
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "20px",
                          "& fieldset": {
                            borderColor: theme.palette.grey[300],
                          },
                        },
                      }}
                    />
                  </TableCell>
                  <TableCell sx={{ py: 1 }}>
                    {/* Empty cell for lastUpdated */}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {showLoader ? (
                  // Skeleton rows shaped like the real ones so the table
                  // doesn't jump when the data arrives.
                  Array.from({ length: Math.min(pageSize, 10) }).map(
                    (_, i) => (
                      <TableRow
                        key={`skeleton-${i}`}
                        aria-busy="true"
                        aria-label={
                          i === 0
                            ? t("common.loading", { defaultValue: "Cargando…" })
                            : undefined
                        }
                      >
                        <TableCell sx={{ maxWidth: 220 }}>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1.25,
                            }}
                          >
                            <Skeleton
                              variant="circular"
                              width={28}
                              height={28}
                              sx={{ flexShrink: 0 }}
                            />
                            <Skeleton
                              variant="text"
                              width={`${55 + ((i * 17) % 35)}%`}
                            />
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Skeleton
                            variant="text"
                            width={`${45 + ((i * 23) % 45)}%`}
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Skeleton
                            variant="rounded"
                            width={40}
                            height={18}
                            sx={{ mx: "auto", borderRadius: "9px" }}
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Skeleton
                            variant="circular"
                            width={22}
                            height={22}
                            sx={{ mx: "auto" }}
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Skeleton
                            variant="rounded"
                            width={48}
                            height={28}
                            sx={{ mx: "auto", borderRadius: "10px" }}
                          />
                        </TableCell>
                        <TableCell>
                          <Skeleton variant="text" width={64} />
                          <Skeleton variant="text" width={64} />
                        </TableCell>
                        <TableCell align="right">
                          <Skeleton
                            variant="text"
                            width={72}
                            sx={{ ml: "auto" }}
                          />
                        </TableCell>
                        <TableCell>
                          <Skeleton variant="text" width={48} />
                        </TableCell>
                      </TableRow>
                    )
                  )
                ) : isEmpty ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary" variant="body2">
                        {t("briefings.placeholder", "No briefings yet")}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  briefings.map((briefing) => (
                    <TableRow
                      hover
                      key={briefing.id}
                      sx={{ cursor: "pointer" }}
                      onClick={() => navigate(`/briefings/${briefing.id}`)}
                      {...middleClickOpen(`/briefings/${briefing.id}`)}
                      // Right click opens the summary drawer alongside the
                      // row's mini menu.
                      onContextMenu={(e) => {
                        handleContextMenu(e, briefing);
                        setSelectedBriefingId(briefing.id);
                      }}
                    >
                      <TableCell sx={{ maxWidth: 220 }}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1.25,
                            minWidth: 0,
                          }}
                        >
                          {/* Brand logo (or placeholder). */}
                          <Box
                            sx={{
                              width: 28,
                              height: 28,
                              flexShrink: 0,
                            }}
                          >
                            {briefing.companyImageBase64 ? (
                              <Box
                                component="img"
                                src={
                                  briefing.companyImageBase64.startsWith(
                                    "data:"
                                  )
                                    ? briefing.companyImageBase64
                                    : `data:image/png;base64,${briefing.companyImageBase64}`
                                }
                                alt=""
                                sx={{
                                  width: 28,
                                  height: 28,
                                  objectFit: "cover",
                                  borderRadius: 9999,
                                }}
                              />
                            ) : (
                              <Box
                                sx={{
                                  width: 28,
                                  height: 28,
                                  borderRadius: 9999,
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  backgroundColor: theme.palette.grey[200],
                                  color: theme.palette.grey[500],
                                }}
                              >
                                <BusinessIcon sx={{ fontSize: 16 }} />
                              </Box>
                            )}
                          </Box>
                          <Typography
                            variant="body2"
                            fontWeight={600}
                            sx={{
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            }}
                            title={briefing.companyName || briefing.companyId}
                          >
                            {briefing.companyName || briefing.companyId}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          sx={{
                            wordBreak: "break-word",
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                          }}
                        >
                          {briefing.name}
                          {(() => {
                            const b = briefing as Record<string, unknown>;
                            if (
                              typeof b.version === "number" &&
                              b.version > 1
                            ) {
                              return (
                                <Chip
                                  label={`v${b.version}`}
                                  size="small"
                                  color="primary"
                                  variant="outlined"
                                  sx={{ height: 20, fontSize: "0.7rem" }}
                                />
                              );
                            }
                            return null;
                          })()}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        {briefing.briefingType &&
                          renderTypeBadge(briefing.briefingType)}
                      </TableCell>
                      <TableCell align="center">
                        {briefing.category &&
                          renderCategoryBadge(briefing.category)}
                      </TableCell>
                      <TableCell align="center">
                        {renderStatusBadge(briefing.status)}
                      </TableCell>
                      <TableCell>
                        {(() => {
                          // Start date, end date below it; dd/mm/yy always.
                          const start = formatShortDate(
                            briefing.campaignsStartDate
                          );
                          const end = formatShortDate(
                            briefing.campaignsEndDate
                          );
                          if (!start && !end) {
                            return (
                              <Typography variant="body2" noWrap>
                                {briefing.month && briefing.year
                                  ? shortMonthYear(
                                      briefing.month,
                                      briefing.year,
                                      i18n.language
                                    )
                                  : "-"}
                              </Typography>
                            );
                          }
                          return (
                            <Box sx={{ fontVariantNumeric: "tabular-nums" }}>
                              <Typography
                                variant="body2"
                                noWrap
                                lineHeight={1.3}
                              >
                                {start || "-"}
                              </Typography>
                              <Typography
                                variant="body2"
                                noWrap
                                lineHeight={1.3}
                              >
                                {end || "-"}
                              </Typography>
                            </Box>
                          );
                        })()}
                      </TableCell>
                      <TableCell align="right">
                        <Typography
                          variant="body2"
                          fontWeight={600}
                          noWrap
                          sx={{ fontVariantNumeric: "tabular-nums" }}
                        >
                          {numberFormatter.format(briefing.totalBudget || 0)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {(() => {
                          const updated = new Date(briefing.updatedAt);
                          const ago = shortTimeAgo(updated, t);
                          return (
                            <Tooltip
                              title={updated.toLocaleString(i18n.language)}
                            >
                              <Typography
                                variant={ago ? "body2" : "caption"}
                                noWrap
                                sx={{ color: "text.secondary" }}
                              >
                                {ago ??
                                  updated.toLocaleDateString(i18n.language)}
                              </Typography>
                            </Tooltip>
                          );
                        })()}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={(e, p) => setPage(p)}
            rowsPerPage={pageSize}
            onRowsPerPageChange={(e) => {
              setPageSize(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[10, 25, 50, 100]}
            labelRowsPerPage={t("common.rowsPerPage", "Rows per page:")}
          />
        </Paper>
      </Box>
      <BriefingSummaryDrawer
        open={!!selectedBriefingId}
        onClose={() => setSelectedBriefingId(null)}
        briefingId={selectedBriefingId}
        canEdit={canCreateBriefings}
      />

      <TableRowContextMenu
        contextMenu={contextMenu}
        onClose={handleCloseContextMenu}
        actions={[
          {
            label: t("common.view", "View"),
            icon: <VisibilityIcon fontSize="small" />,
            onClick: () => {
              if (contextMenu?.data)
                navigate(`/briefings/${contextMenu.data.id}`);
            },
          },
          {
            label: t("common.openInNewTab", "Open in new tab"),
            icon: <LaunchIcon fontSize="small" />,
            onClick: () => {
              if (contextMenu?.data)
                window.open(`/briefings/${contextMenu.data.id}`, "_blank");
            },
          },
          {
            label: t("common.edit", "Edit"),
            icon: <EditIcon fontSize="small" />,
            show:
              canCreateBriefings && contextMenu?.data?.status !== "FINALIZED",
            onClick: () => {
              if (contextMenu?.data)
                navigate(`/briefings/${contextMenu.data.id}/edit`);
            },
          },
          {
            label: t("common.delete", "Delete"),
            icon: <DeleteIcon fontSize="small" />,
            color: "error.main",
            show:
              canCreateBriefings && contextMenu?.data?.status !== "FINALIZED",
            onClick: () => {
              if (contextMenu?.data) setBriefingToDelete(contextMenu.data.id);
            },
          },
        ]}
      />

      <ConfirmationModal
        open={!!briefingToDelete}
        title={t("briefings.deleteConfirmTitle", "Delete briefing")}
        message={t(
          "briefings.deleteConfirmMessage",
          "Are you sure you want to delete this briefing? This action cannot be undone."
        )}
        onConfirm={handleConfirmDelete}
        onCancel={() => setBriefingToDelete(null)}
        isLoading={isDeleting}
      />

      <ConfirmationModal
        open={isSyncModalOpen}
        title={t("briefings.syncModalTitle", "Sync Financial Calculations")}
        message={t(
          "briefings.syncModalContent",
          "This will recalculate expected volume, visibility, CTR and adserver costs for older campaigns based on current platform defaults. Manual margin values are preserved. Proceed?"
        )}
        onConfirm={handleSyncFinancials}
        onCancel={() => setIsSyncModalOpen(false)}
        isLoading={isSyncing}
      />

      <Dialog
        open={isExportModalOpen}
        onClose={() => !isExporting && setIsExportModalOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3, p: 1 },
        }}
      >
        <DialogTitle sx={{ fontFamily: "Montserrat", fontWeight: 700 }}>
          {t("briefings.exportPricing", "Export Pricing CSV")}
        </DialogTitle>
        <DialogContent
          sx={{ display: "flex", flexDirection: "column", gap: 3, mt: 1 }}
        >
          <FormControl fullWidth>
            <InputLabel id="export-variant-label">
              {t("briefings.exportVariant", "Export type")}
            </InputLabel>
            <Select
              labelId="export-variant-label"
              label={t("briefings.exportVariant", "Export type")}
              value={exportVariant}
              onChange={(e) =>
                setExportVariant(e.target.value as "pricing" | "closing")
              }
            >
              <MenuItem value="pricing">
                {t("briefings.exportVariantPricing", "Pricing (per briefing)")}
              </MenuItem>
              <MenuItem value="closing">
                {t(
                  "briefings.exportVariantClosing",
                  "Month-end closing (per associated campaign)"
                )}
              </MenuItem>
            </Select>
          </FormControl>
          <Typography variant="body2" color="text.secondary">
            {exportVariant === "closing"
              ? t(
                  "briefings.exportClosingDesc",
                  "One row per associated campaign, with client, campaign, platform, format, funnel stage and dates, plus planned and real investment with margin applied and the variance between them. Amounts are prorated to the chosen range."
                )
              : t(
                  "briefings.exportPricingDesc",
                  "Select the date range to export. Amounts are prorated to the chosen range."
                )}
          </Typography>
          <TextField
            type="date"
            fullWidth
            label={t("common.startDate", "Start date")}
            value={exportStartDate}
            onChange={(e) => setExportStartDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            inputProps={{ max: exportEndDate || undefined }}
          />
          <TextField
            type="date"
            fullWidth
            label={t("common.endDate", "End date")}
            value={exportEndDate}
            onChange={(e) => setExportEndDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            inputProps={{ min: exportStartDate || undefined }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setIsExportModalOpen(false)}
            color="inherit"
            disabled={isExporting}
            sx={{
              fontWeight: 600,
              textTransform: "none",
              borderRadius: "50px",
            }}
          >
            {t("common.cancel", "Cancel")}
          </Button>
          <Button
            onClick={() => {
              handleExportControlPanel();
              setIsExportModalOpen(false);
            }}
            variant="contained"
            color="primary"
            disabled={
              isExporting ||
              !exportStartDate ||
              !exportEndDate ||
              exportEndDate < exportStartDate
            }
            startIcon={
              isExporting ? <CircularProgress size={20} /> : <DownloadIcon />
            }
            sx={{
              fontWeight: 700,
              textTransform: "none",
              borderRadius: "50px",
            }}
          >
            {isExporting
              ? t("common.exporting", "Exporting...")
              : t("common.download", "Download")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Briefings;
